import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Home, Loader2, Pencil } from "lucide-react";
import { SiteLogo } from "@/components/SiteLogo";
import { BrandText } from "@/components/BrandText";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SectionsView, SectionsEditor } from "@/components/SectionBlocks";
import { ContactSection } from "@/components/ContactSection";
import { EditableText } from "@/components/EditableText";

const PAGE_META = {
  "chi-siamo": { label: "Chi Siamo", tag: "La nostra associazione e i nostri valori" },
  "contatti": { label: "Contatti", tag: "Come raggiungerci e scriverci" },
};

export default function CustomPage() {
  const { key } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const meta = PAGE_META[key];

  useEffect(() => {
    if (!meta) return;
    setLoading(true);
    api.get(`/page-content/${key}`).then((r) => setSections(r.data.sections || [])).catch(() => {}).finally(() => setLoading(false));
  }, [key, meta]);

  if (!meta) return <Navigate to="/" replace />;

  const save = async (draft) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/page-content/${key}`, { sections: draft });
      setSections(data.sections || draft);
      setEditMode(false);
      toast.success("Pagina salvata");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground noise-bg">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
            <SiteLogo className="w-9 h-9" />
            <div className="flex flex-col leading-tight">
              <BrandText />
            </div>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-gold transition-colors inline-flex items-center gap-1.5"><Home className="w-4 h-4" /> Home</Link>
        </div>
      </header>

      <section className="relative border-b border-white/10 bg-[#0B1220]/60">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <span className="overline text-gold uppercase text-xs tracking-[0.25em]"><EditableText slot={`page_${key}_overline`} defaultText="OPES Ginnastiche" /></span>
          <h1 data-testid="custom-page-title" className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-2"><EditableText slot={`page_${key}_title`} defaultText={meta.label} /></h1>
          <p className="text-muted-foreground mt-2"><EditableText slot={`page_${key}_tag`} defaultText={meta.tag} /></p>
        </div>
      </section>

      {loading ? (
        <div className="flex justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
      ) : (
        <main className="max-w-5xl mx-auto px-6 py-14 space-y-10">
          {key === "contatti" && <ContactSection />}
          {isAdmin && !editMode && (
            <div className="flex justify-end">
              <Button data-testid="edit-page-btn" size="sm" onClick={() => setEditMode(true)} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold"><Pencil className="w-3.5 h-3.5 mr-1.5" /> Modifica pagina</Button>
            </div>
          )}
          {editMode ? (
            <SectionsEditor initial={sections} onSave={save} onCancel={() => setEditMode(false)} saving={saving} />
          ) : sections.length > 0 ? (
            <SectionsView sections={sections} />
          ) : key === "contatti" ? null : (
            <div className="text-center py-20 text-muted-foreground border border-dashed border-white/10 rounded-xl">
              {isAdmin ? 'Pagina vuota. Usa "Modifica pagina" per aggiungere contenuti.' : "Contenuto in arrivo."}
            </div>
          )}
        </main>
      )}
    </div>
  );
}
